import React from "react";
import { CheckCircle, PencilSimple, Trash, ArrowUp, ArrowDown, Sparkle, Power } from "@phosphor-icons/react";
import { condParts, actParts } from "./ruleText";
import { Checkbox } from "@/components/ui/checkbox";

function Token({ children, tone = "field" }) {
  const cls = {
    field: "text-white",
    op: "text-neutral-500",
    value: "text-[#73bf44] font-mono text-[0.78rem]",
    then: "text-amber-300",
  }[tone];
  return <span className={cls}>{children}</span>;
}

function Clause({ parts }) {
  return (
    <span className="inline">
      <Token>{parts.field}</Token> <Token tone="op">{parts.op}</Token>{parts.value && <> <Token tone="value">{parts.value}</Token></>}
    </span>
  );
}

export function RuleSentence({ rule, schema, hubName }) {
  const conds = rule.conditions.map((c) => condParts(c, schema, hubName));
  const acts = rule.actions.map((a) => actParts(a, schema, hubName));
  return (
    <div className="text-sm leading-relaxed" data-testid="rule-sentence">
      <span className="text-[0.65rem] font-bold uppercase tracking-wider text-neutral-500 mr-1.5">If</span>
      {conds.length ? conds.map((p, i) => (
        <React.Fragment key={i}>{i > 0 && <span className="text-[0.65rem] font-bold text-neutral-600 mx-1.5">AND</span>}<Clause parts={p} /></React.Fragment>
      )) : <span className="text-neutral-400">any van</span>}
      <div className="mt-1">
        <span className="text-[0.65rem] font-bold uppercase tracking-wider text-amber-300/80 mr-1.5">Then</span>
        {acts.map((p, i) => (
          <React.Fragment key={i}>{i > 0 && <span className="text-neutral-600 mx-1">·</span>}<Clause parts={p} /></React.Fragment>
        ))}
      </div>
    </div>
  );
}

function Btn({ onClick, title, children, testId, danger }) {
  return (
    <button type="button" onClick={onClick} title={title} data-testid={testId}
      className={`p-1.5 rounded-lg text-neutral-500 transition-colors ${danger ? "hover:text-red-400 hover:bg-red-500/10" : "hover:text-white hover:bg-white/[0.06]"}`}>
      {children}
    </button>
  );
}

export function RuleCard({ rule, schema, hubName, onEdit, onApprove, onDelete, onToggle, onMove, compact, selecting, checked, onCheck }) {
  const draft = rule.status === "draft";
  const off = !rule.enabled && !draft;
  const tone = checked ? "border-[#73bf44] bg-[#73bf44]/[0.06]" : draft ? "border-amber-500/40 bg-amber-500/[0.04]" : off ? "border-neutral-800 bg-black/20 opacity-60" : "border-neutral-800 bg-[#121212] hover:border-neutral-700";
  return (
    <div data-testid="rule-card" data-rule-id={rule.id} data-selected={checked ? "true" : "false"}
      onClick={selecting ? () => onCheck(rule) : undefined}
      className={`group rounded-xl border px-4 py-3 transition-colors ${selecting ? "cursor-pointer select-none" : ""} ${tone}`}>
      <div className="flex items-start gap-3">
        {selecting && (
          <Checkbox checked={!!checked} onCheckedChange={() => onCheck(rule)} onClick={(e) => e.stopPropagation()} aria-label={`Select ${rule.title}`}
            data-testid="rule-select-checkbox" className="mt-0.5 border-neutral-600 data-[state=checked]:bg-[#73bf44] data-[state=checked]:border-[#73bf44] data-[state=checked]:text-[#050505]" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="font-heading text-sm font-semibold text-white" data-testid="rule-title">{rule.title}</span>
            {draft && <span className="inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-wider text-amber-300"><Sparkle size={11} weight="fill" /> {rule.replaces ? "Draft · replaces a rule" : "Draft"}</span>}
            {off && <span className="text-[0.6rem] font-bold uppercase tracking-wider text-neutral-500">Off</span>}
            <span className="text-[0.65rem] text-neutral-500" data-testid="rule-van-count">{rule.van_count ?? "…"} {rule.van_count === 1 ? "van" : "vans"}</span>
          </div>
          <RuleSentence rule={rule} schema={schema} hubName={hubName} />
          {rule.description && !compact && <p className="text-xs text-neutral-500 mt-1.5 line-clamp-2">{rule.description}</p>}
        </div>
        {!selecting && <div className={`flex items-center gap-0.5 shrink-0 ${compact ? "" : "opacity-60 group-hover:opacity-100"} transition-opacity`}>
          {draft && (
            <button onClick={() => onApprove(rule)} data-testid="rule-approve" className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-full bg-[#73bf44] text-[#050505] hover:bg-[#5fa338] mr-1">
              <CheckCircle size={13} weight="fill" /> Approve
            </button>
          )}
          {onMove && !draft && <><Btn onClick={() => onMove(rule, "up")} title="Move up" testId="rule-move-up"><ArrowUp size={14} /></Btn><Btn onClick={() => onMove(rule, "down")} title="Move down" testId="rule-move-down"><ArrowDown size={14} /></Btn></>}
          {onToggle && !draft && <Btn onClick={() => onToggle(rule)} title={rule.enabled ? "Turn off" : "Turn on"} testId="rule-toggle"><Power size={14} /></Btn>}
          <Btn onClick={() => onEdit(rule)} title="Edit" testId="rule-edit"><PencilSimple size={14} /></Btn>
          <Btn onClick={() => onDelete(rule)} title={draft ? "Discard" : "Delete"} testId="rule-delete" danger><Trash size={14} /></Btn>
        </div>}
      </div>
    </div>
  );
}
